import { createRef, PureComponent } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { type ActionButtonProps, LoadingSpinner } from "widgets";

import { type ISelectOption, simpleDropdownFieldValidation } from "@/app-utils/form.ts";
import { type ISelectionModel, type TGridSelModelIdentifier } from "@/app-utils/selection-model.ts";
import { type InputUserAccountInput, type Role, type User } from "@/graphql.ts";
import { type IIdentifiable } from "@/redux/modules/table-data.ts";
import {
  addUsers,
  AUTHENTICATION_MODE,
  loadPotentialLdapUsers,
  noEnoughFreeSeatsText,
  noMoreFreeSeatsText,
} from "@/redux/modules/users-admin.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { CELL_TYPE } from "@/settings/settings.ts";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormNote } from "@/ui-modules/core/form/layout/FormNote.js";

import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";
import { AddNewUserDialogImpl } from "./AddNewUserDialog.js";

interface IAddLdapUsersFormOwnProps {
  roles: Role[];
  updateDialogState: (state) => void;
  numberOfFreeSeats: number;
  onClose: () => void;
  refreshListOfUsers: () => void;
}

interface IAddLdapUsersFormDispatchProps {
  createUsers: (usersInput: InputUserAccountInput[]) => Promise<boolean>;
  loadPotentialUsers: () => Promise<User[]>;
}

interface IAddLdapUsersFormAppStateProps {
  initialValues: Partial<any>;
  potentialUsers: User[];
  ldapAuthenticationModeEnabled: boolean;
}

export interface IAddLdapUsersFormProps
  extends IAddLdapUsersFormAppStateProps,
    IAddLdapUsersFormDispatchProps,
    IAddLdapUsersFormOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState): IAddLdapUsersFormAppStateProps {
  return {
    initialValues: {
      [AddLdapUsersFormImpl.ADD_LDAP_USERS_FORM_FIELD_STATUS]: 1,
    },
    potentialUsers: state.usersAdmin.potentialLdapUsers,
    ldapAuthenticationModeEnabled:
      state.config.configuration &&
      state.config.configuration.authenticationMode &&
      state.config.configuration.authenticationMode === AUTHENTICATION_MODE.LDAP,
  };
}

function mapDispatchToProps(dispatch): IAddLdapUsersFormDispatchProps {
  return {
    createUsers: (usersInput: InputUserAccountInput[]) => dispatch(addUsers(usersInput)),
    loadPotentialUsers: () => dispatch(loadPotentialLdapUsers()),
  };
}

interface IAddLdapUsersFormState {
  isLoadingPotentialUsers: boolean;
  selectionCount: number;
  userFilterTerm: string;
}

class AddLdapUsersFormImpl extends PureComponent<IAddLdapUsersFormProps, IAddLdapUsersFormState> {
  public static ADD_LDAP_USERS_FORM_NAME = "addLdapUsersForm";

  // field names
  public static ADD_LDAP_USERS_FORM_FIELD_ROLES = "roles";
  public static ADD_LDAP_USERS_FORM_FIELD_STATUS = "accountStatus";

  private wannaClose = false; // allows to discrimate between the 2 buttons pushed for submission, so that we know which one to "spin"
  private tableInstance = createRef<InMemoryTableWithSelectionModel>();

  private static ldapUsersColumnDefs: any[] = [
    {
      name: "Username",
      field: "username",
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "First Name",
      field: "firstName",
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Last Name",
      field: "lastName",
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Email",
      field: "email",
      cellType: CELL_TYPE.STRING,
    },
  ];

  constructor(props: IAddLdapUsersFormProps) {
    super(props);

    this.state = {
      isLoadingPotentialUsers: false,
      selectionCount: 0,
      userFilterTerm: null,
    };
  }

  initButtonProps = () => {
    this.props.updateDialogState({
      nextButtonProps: this.buildCreateAndCloseButtonProps(),
      otherButtonProps: this.buildCreateAndAddMoreButtonProps(),
    });
  };

  loadPotentialUsers = () => {
    this.setState({ isLoadingPotentialUsers: true });
    this.props.loadPotentialUsers().then(() => this.setState({ isLoadingPotentialUsers: false }));
  };

  componentDidMount() {
    this.loadPotentialUsers();
    this.initButtonProps();
  }

  handleSubmitDataAndClose = (values): Promise<void> => {
    this.wannaClose = true;
    return this.handleSubmitData(values).then(() => {
      this.props.refreshListOfUsers();
      this.props.onClose();
    });
  };

  handleSubmitDataAndAddAnother = (values): Promise<void> => {
    this.wannaClose = false;
    return this.handleSubmitData(values).then(() => {
      this.initButtonProps();
      this.props.reset();
      this.props.refreshListOfUsers();
    });
  };

  componentDidUpdate(prevProps: IAddLdapUsersFormProps, prevState: IAddLdapUsersFormState) {
    if (
      this.state.selectionCount !== prevState.selectionCount ||
      this.props.submitting !== prevProps.submitting ||
      this.props.error !== prevProps.error
    ) {
      this.props.updateDialogState({
        nextButtonProps: this.buildCreateAndCloseButtonProps(),
        otherButtonProps: this.buildCreateAndAddMoreButtonProps(),
        error: this.props.error,
      });
    }
  }

  handleSubmitData = (values): Promise<boolean> => {
    const selectedUserIds: TGridSelModelIdentifier[] = this.tableInstance.current.getSelectedIds();

    if (selectedUserIds.length === 0) {
      throw new SubmissionError({ _error: "No user selected" });
    }

    const selectedRoleIds: number[] = values[AddLdapUsersFormImpl.ADD_LDAP_USERS_FORM_FIELD_ROLES].map(
      (role: Role) => role.id,
    );
    const selectedStatus: ISelectOption = values[AddLdapUsersFormImpl.ADD_LDAP_USERS_FORM_FIELD_STATUS];

    const payload: InputUserAccountInput[] = selectedUserIds.map((selectedUserName: string) => {
      const selectedPotentialUser: User = this.props.potentialUsers?.find(
        (potentialUser: User) => potentialUser.username === selectedUserName,
      );
      return {
        username: selectedPotentialUser.username?.trim(),
        firstName: selectedPotentialUser.firstName,
        lastName: selectedPotentialUser.lastName,
        email: selectedPotentialUser.email,
        roles: selectedRoleIds,
        isActive: selectedStatus && selectedStatus.value === 1,
        authenticationMode: AUTHENTICATION_MODE.LDAP,
      };
    });

    return this.props.createUsers(payload).then(
      (user: any) => {
        this.loadPotentialUsers();
        return user;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  buildCreateAndAddMoreButtonProps = (): ActionButtonProps => {
    const noMoreFreeSeats: boolean = this.props.numberOfFreeSeats === 0;
    const notEnoughSeats: boolean =
      this.props.numberOfFreeSeats > 0 && this.props.numberOfFreeSeats - this.state.selectionCount < 1;

    return {
      label: "Create and Add More",
      isLoading: !this.wannaClose && this.props.submitting,
      isDisabled:
        !this.props.ldapAuthenticationModeEnabled || this.props.submitting || noMoreFreeSeats || notEnoughSeats,
      tooltip: noMoreFreeSeats
        ? noMoreFreeSeatsText()
        : notEnoughSeats
          ? noEnoughFreeSeatsText(this.props.numberOfFreeSeats)
          : "Add selected users and keep the dialog open to add more",
      onPress: this.props.handleSubmit(this.handleSubmitDataAndAddAnother),
    };
  };

  buildCreateAndCloseButtonProps = (): ActionButtonProps => {
    const noMoreFreeSeats: boolean = this.props.numberOfFreeSeats === 0;
    const notEnoughSeats: boolean =
      this.props.numberOfFreeSeats > 0 && this.props.numberOfFreeSeats - this.state.selectionCount < 0;

    return {
      label: "Create and Close",
      isLoading: this.wannaClose && this.props.submitting,
      isDisabled:
        !this.props.ldapAuthenticationModeEnabled || this.props.submitting || noMoreFreeSeats || notEnoughSeats,
      tooltip: noMoreFreeSeats
        ? noMoreFreeSeatsText()
        : notEnoughSeats
          ? noEnoughFreeSeatsText(this.props.numberOfFreeSeats)
          : "Add selected users and close the dialog",
      onPress: this.props.handleSubmit(this.handleSubmitDataAndClose),
    };
  };

  selectionModelUpdated = (selectionModel: ISelectionModel) => {
    this.setState({ selectionCount: selectionModel.selectedIds.length });
  };

  userPropContainsTerm = (term: string, user: User): boolean => {
    return (
      user.username.includes(term) ||
      (user.firstName && user.firstName.includes(term)) ||
      (user.lastName && user.lastName.includes(term)) ||
      (user.email && user.email.includes(term))
    );
  };

  createRowData = (users: User[]): IIdentifiable[] => {
    return users
      ?.filter(
        (user: User) =>
          this.state.userFilterTerm === null || this.userPropContainsTerm(this.state.userFilterTerm, user),
      )
      .map((user: User) => {
        return {
          ...user,
          id: user.username,
        };
      });
  };

  onFilterChanged = (filterTerm: string) => {
    return new Promise<void>((resolve) => {
      if (filterTerm.length === 0) {
        this.setState({ userFilterTerm: "" });
        resolve();
      }
      this.setState({ userFilterTerm: filterTerm });
      resolve();
    });
  };

  missingDirectoryServiceConfigurationPlaceholderText = () => {
    return (
      <span>
        <p>This system is not linked to a directory service (such as LDAP).</p>
        <br />
        <p>
          If you wish to use a directory service for user management and authentication, please contact Genedata support
          for assistance.
        </p>
      </span>
    );
  };

  render() {
    return !this.props.ldapAuthenticationModeEnabled ? (
      <ContentPlaceholder text={this.missingDirectoryServiceConfigurationPlaceholderText()} />
    ) : (
      <SelForm>
        <FormNote>
          The following users can be added to Selector via LDAP, select one or more users in the list to create the
          corresponding Selector accounts.
        </FormNote>

        <FormInputGroup>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <FormLabel>
              Directory Users <span className="count">({this.state.selectionCount} selected)</span>
            </FormLabel>
            <div style={{ marginBottom: "5px" }}>
              <AsyncSearchWithExprParser onSearchExprChange={this.onFilterChanged} />
            </div>
          </div>

          <FormInputWidget>
            <span style={{ display: "block", position: "relative", height: "300px" }}>
              {this.state.isLoadingPotentialUsers ? (
                <LoadingSpinner inCenter />
              ) : (
                <InMemoryTableWithSelectionModel
                  columns={AddLdapUsersFormImpl.ldapUsersColumnDefs}
                  ref={this.tableInstance}
                  rowGridData={this.createRowData(this.props.potentialUsers)}
                  uniqueGridIdentifier={"potential-ldap-users"}
                  useSelectionModel={true}
                  noRowsMessage={"No potential user found."}
                  selectionModelUpdated={this.selectionModelUpdated}
                />
              )}
            </span>
          </FormInputWidget>
        </FormInputGroup>

        <ReactSelectReduxFormField
          name={AddLdapUsersFormImpl.ADD_LDAP_USERS_FORM_FIELD_ROLES}
          isMulti
          closeMenuOnSelect={false}
          isClearable={false}
          isSearchable={true}
          getOptionValue={(option: Role) => option.id}
          getOptionLabel={(option: Role) => option.name}
          label={"Roles"}
          tooltip={"Select one or multiple roles..."}
          placeholder={"Select one or multiple roles..."}
          component={ReactSelectField}
          options={this.props.roles}
          validate={simpleDropdownFieldValidation}
        />

        <ReactSelectReduxFormField
          name={AddLdapUsersFormImpl.ADD_LDAP_USERS_FORM_FIELD_STATUS}
          label={"Account status"}
          component={ReactSelectField}
          options={AddNewUserDialogImpl.statusOptions}
          isSearchable
          closeMenuOnSelect
          openMenuOnFocus
          isClearable={true}
          validate={simpleDropdownFieldValidation}
        />
      </SelForm>
    );
  }
}

const AddLdapUsersForm = connect<
  IAddLdapUsersFormAppStateProps,
  IAddLdapUsersFormDispatchProps,
  IAddLdapUsersFormOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: AddLdapUsersFormImpl.ADD_LDAP_USERS_FORM_NAME,
  })(AddLdapUsersFormImpl) as any,
);

export { AddLdapUsersFormImpl };
export default AddLdapUsersForm;
